import type { ActionFlowUserOutcomeProof } from "./actionflow-user-outcome-proof";

export const ACTIONFLOW_BROWSER_TRACE_CONTRACT_SCHEMA_VERSION =
  "actionflow-browser-trace-contract.v1";

export const ACTIONFLOW_BROWSER_TRACE_PROOF_SCHEMA_VERSION =
  "actionflow-browser-trace-proof.v1";

export interface ActionFlowBrowserTraceBinding {
  id: string;
  selector: string;
  statePath: string;
  expectedText: string;
  requireChange?: boolean;
  evidenceRefs: string[];
}

export interface ActionFlowBrowserTraceContract {
  schemaVersion: typeof ACTIONFLOW_BROWSER_TRACE_CONTRACT_SCHEMA_VERSION;
  scenarioId: string;
  userGoal: string;
  userValue: string;
  triggerLabel: string;
  bindings: ActionFlowBrowserTraceBinding[];
  evidenceRefs: string[];
}

export interface ActionFlowBrowserTraceObservation {
  bindingId: string;
  selector: string;
  beforeText: string | null;
  afterText: string | null;
  selectorFound: boolean;
  changed: boolean;
}

export interface ActionFlowBrowserTraceEvaluation {
  bindingId: string;
  selector: string;
  statePath: string;
  expectedText: string;
  beforeText: string | null;
  afterText: string | null;
  passed: boolean;
  evidenceRefs: string[];
  message?: string;
}

export interface ActionFlowBrowserTraceProof {
  schemaVersion: typeof ACTIONFLOW_BROWSER_TRACE_PROOF_SCHEMA_VERSION;
  contractVersion: typeof ACTIONFLOW_BROWSER_TRACE_CONTRACT_SCHEMA_VERSION;
  scenarioId: string;
  userGoal: string;
  userValue: string;
  evaluatedAt: string;
  ok: boolean;
  readiness: "ready" | "blocked";
  triggerLabel: string;
  evaluations: ActionFlowBrowserTraceEvaluation[];
  blockingFailures: string[];
  userOutcomeReadiness: ActionFlowUserOutcomeProof["readiness"];
  evidenceRefs: string[];
}

export interface BuildActionFlowBrowserTraceProofOptions {
  evaluatedAt?: string;
}

export function buildActionFlowBrowserTraceProof(
  contract: ActionFlowBrowserTraceContract,
  userOutcomeProof: ActionFlowUserOutcomeProof,
  observations: ActionFlowBrowserTraceObservation[],
  options: BuildActionFlowBrowserTraceProofOptions = {},
): ActionFlowBrowserTraceProof {
  const observationByBinding = new Map(
    observations.map((observation) => [observation.bindingId, observation]),
  );
  const evaluations = contract.bindings.map((binding) =>
    evaluateBinding(binding, observationByBinding.get(binding.id)),
  );
  const blockingFailures = evaluations
    .filter((evaluation) => !evaluation.passed)
    .map((evaluation) => evaluation.message ?? `${evaluation.bindingId} failed`);

  if (!userOutcomeProof.ok) {
    blockingFailures.unshift(
      userOutcomeProof.blockingFailures[0] ??
        "User outcome proof must be ready before browser trace proof.",
    );
  }

  return {
    schemaVersion: ACTIONFLOW_BROWSER_TRACE_PROOF_SCHEMA_VERSION,
    contractVersion: ACTIONFLOW_BROWSER_TRACE_CONTRACT_SCHEMA_VERSION,
    scenarioId: contract.scenarioId,
    userGoal: contract.userGoal,
    userValue: contract.userValue,
    evaluatedAt: options.evaluatedAt ?? new Date().toISOString(),
    ok: blockingFailures.length === 0,
    readiness: blockingFailures.length === 0 ? "ready" : "blocked",
    triggerLabel: contract.triggerLabel,
    evaluations,
    blockingFailures,
    userOutcomeReadiness: userOutcomeProof.readiness,
    evidenceRefs: [...contract.evidenceRefs],
  };
}

function evaluateBinding(
  binding: ActionFlowBrowserTraceBinding,
  observation: ActionFlowBrowserTraceObservation | undefined,
): ActionFlowBrowserTraceEvaluation {
  const base = {
    bindingId: binding.id,
    selector: binding.selector,
    statePath: binding.statePath,
    expectedText: binding.expectedText,
    beforeText: observation?.beforeText ?? null,
    afterText: observation?.afterText ?? null,
    evidenceRefs: binding.evidenceRefs,
  };
  if (!observation?.selectorFound) {
    return {
      ...base,
      passed: false,
      message: `${binding.selector} must exist in browser trace`,
    };
  }
  if (binding.requireChange !== false && !observation.changed) {
    return {
      ...base,
      passed: false,
      message: `${binding.selector} must change after ${binding.statePath}`,
    };
  }
  if (observation.afterText !== binding.expectedText) {
    return {
      ...base,
      passed: false,
      message: `${binding.selector} text must equal ${binding.expectedText}`,
    };
  }
  return {
    ...base,
    passed: true,
  };
}
